/*
 * Mirelia — Compra da Meta para o funil pago contra entrega (Releasit).
 *
 * A loja não passa pelo checkout do Shopify. No sucesso do formulário a Releasit
 * já dispara `Purchase` para o Pixel da Meta, mas (a) logo antes de redirecionar
 * o cliente para uma página em shopify.com, o que às vezes corta o envio, e
 * (b) sem correspondência avançada.
 *
 * Este script envolve a função de tracking da Releasit e envia um `Purchase`
 * próprio direto para `https://www.facebook.com/tr/` — antes do redirect e com
 * e-mail/telefone/nome do pedido hasheados. Usa o mesmo `eventID` da Releasit
 * (`order.id`), então a Meta funde os dois num só evento e fica com a melhor
 * informação. Não emite PageView, ViewContent nem AddToCart — esses já vêm do
 * canal Facebook & Instagram e da própria Releasit.
 */

const META_PIXEL_ID = '1348924887439435';
const PIXEL_ENDPOINT = 'https://www.facebook.com/tr/';
const FALLBACK_CURRENCY = 'EUR';
const PIXEL_VERSION = '2.9.107';

const REDIRECT_HOLD_MS = 300;
const RELEASIT_POLL_INTERVAL_MS = 200;
const RELEASIT_POLL_ATTEMPTS = 75;
const SENT_EVENT_STORAGE_PREFIX = 'mirelia_meta_evt_';

const sentEventIds = new Set();

function readCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : '';
}

function browserClickId() {
  const stored = readCookie('_fbc');
  if (stored) return stored;
  const clickId = new URLSearchParams(window.location.search).get('fbclid');
  return clickId ? `fb.1.${Date.now()}.${clickId}` : '';
}

function appendNamespaced(params, namespace, data) {
  Object.entries(data || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    const serialised = typeof value === 'object' ? JSON.stringify(value) : String(value);
    params.set(`${namespace}[${key}]`, serialised);
  });
}

function baseParams() {
  const params = new URLSearchParams({
    id: META_PIXEL_ID,
    ev: 'Purchase',
    dl: window.location.href,
    rl: document.referrer,
    ts: String(Date.now()),
    v: PIXEL_VERSION,
    r: 'stable',
  });
  const browserId = readCookie('_fbp');
  if (browserId) params.set('fbp', browserId);
  const clickId = browserClickId();
  if (clickId) params.set('fbc', clickId);
  return params;
}

function purchaseUrl(customData, extraParams) {
  const params = baseParams();
  appendNamespaced(params, 'cd', customData);
  Object.entries(extraParams || {}).forEach(([key, value]) => params.set(key, value));
  return `${PIXEL_ENDPOINT}?${params.toString()}`;
}

function firePixel(url) {
  try {
    window.fetch(url, { mode: 'no-cors', credentials: 'include', keepalive: true });
  } catch (networkError) {
    new Image().src = url;
  }
}

function alreadySent(eventId) {
  if (sentEventIds.has(eventId)) return true;
  try {
    return window.localStorage.getItem(SENT_EVENT_STORAGE_PREFIX + eventId) === '1';
  } catch (storageError) {
    return false;
  }
}

function markSent(eventId) {
  sentEventIds.add(eventId);
  try {
    window.localStorage.setItem(SENT_EVENT_STORAGE_PREFIX + eventId, '1');
  } catch (storageError) {
    // localStorage indisponível (navegação privada): dedupe fica só em memória.
  }
}

async function sha256Hex(value) {
  const normalised = String(value).trim().toLowerCase();
  if (!normalised) return '';
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalised));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function matchableFields(order) {
  const address = order.shipping_address || {};
  return {
    em: order.email,
    ph: String(address.phone || '').replace(/\D/g, ''),
    fn: address.first_name,
    ln: address.last_name,
    ct: address.city,
    zp: address.zip,
  };
}

async function hashedMatchParams(order) {
  const entries = Object.entries(matchableFields(order));
  const hashed = await Promise.all(entries.map(([, raw]) => sha256Hex(raw)));
  const params = {};
  entries.forEach(([key], index) => {
    if (hashed[index]) params[`ud[${key}]`] = hashed[index];
  });
  return params;
}

function orderValue(order) {
  const raw =
    order.total_price ??
    order.total_price_set?.shop_money?.amount ??
    0;
  return Number(String(raw).replace(',', '.')) || 0;
}

function purchaseCustomData(order) {
  const lineItems = order.line_items || [];
  return {
    value: orderValue(order),
    currency: order.currency || FALLBACK_CURRENCY,
    content_type: 'product',
    content_ids: lineItems.map((item) => String(item.product_id || item.variant_id || item.id)),
    num_items: lineItems.reduce((total, item) => total + (item.quantity || 0), 0),
  };
}

async function trackPurchase(order) {
  if (!order || !(order.id || order.name)) return;
  const eventId = String(order.id || order.name);
  if (alreadySent(eventId)) return;
  markSent(eventId);
  const matching = await hashedMatchParams(order).catch(() => ({}));
  firePixel(purchaseUrl(purchaseCustomData(order), { eid: eventId, ...matching }));
}

function wrapReleasitPurchase(handlers) {
  const originalPurchase = handlers.purchase;
  handlers.mireliaHooked = true;
  handlers.purchase = function mireliaPurchase(payload) {
    const forwardArgs = arguments;
    const proceed = () => originalPurchase.apply(handlers, forwardArgs);
    Promise.race([
      trackPurchase(payload && payload.order).catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, REDIRECT_HOLD_MS)),
    ]).then(proceed, proceed);
  };
}

function hookReleasitPurchase() {
  const handlers = window._rsi?.tracking?.eventsHanlders;
  if (!handlers || handlers.mireliaHooked || typeof handlers.purchase !== 'function') {
    return Boolean(handlers?.mireliaHooked);
  }
  wrapReleasitPurchase(handlers);
  return true;
}

function waitForReleasit(attempt = 0) {
  if (hookReleasitPurchase() || attempt >= RELEASIT_POLL_ATTEMPTS) return;
  setTimeout(() => waitForReleasit(attempt + 1), RELEASIT_POLL_INTERVAL_MS);
}

function init() {
  try {
    waitForReleasit();
  } catch (setupError) {
    // Rastreamento nunca deve quebrar a loja.
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
